import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { fetchAllUsers } from '../services/usersApi';
import { passwordProblems, generateTemporaryPassword } from '../utils/passwordPolicy';
import PasswordChecklist from '../components/PasswordChecklist';
import Modal from '../components/Modal';
import { formatIsoDate } from '../utils/formatDateValue';
import { PlusIcon, TrashIcon, PencilIcon, KeyIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import UserAvatar from '../components/UserAvatar';
import useCountries from '../hooks/useCountries';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'DOCUMENTER' | 'RESEARCHER' | 'APPROVER' | 'ADMIN';
  organization?: string;
  isActive: boolean;
  mfaEnabled?: boolean;
  countries?: string[];
  lastLogin: string | null;
  mustChangePassword?: boolean;
  createdAt: string;
  profilePicture?: string | null;
}

const Users: React.FC = () => {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const { user: currentUser } = useAuth();
  const { countries: countryOptions } = useCountries();

  // Form state
  const [formData, setFormData] = useState<{
    email: string;
    firstName: string;
    lastName: string;
    role: 'DOCUMENTER' | 'RESEARCHER' | 'APPROVER' | 'ADMIN';
    password: string;
    organization: string;
    countries: string[];
  }>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'DOCUMENTER',
    password: '',
    organization: '',
    countries: []
  });
  const [countryInput, setCountryInput] = useState('');
  // What each role can do, in the words the confirm dialog and the form use.
  const ROLE_DESCRIPTIONS: Record<string, string> = {
    DOCUMENTER: 'Enters cases and sees only their own submissions.',
    RESEARCHER: 'Enters and edits cases, runs research on them, decides duplicates, and sees cases in their country scope.',
    APPROVER: 'Everything a Researcher can do, plus approving and rejecting cases and seeing every case.',
    ADMIN: 'Everything, including managing users and the scraper.',
  };
  // Per-user activity (audit rows), shown in a modal.
  const [activityUser, setActivityUser] = useState<User | null>(null);
  const [activity, setActivity] = useState<any[] | null>(null);
  const openActivity = async (u: User) => {
    setActivityUser(u); setActivity(null);
    try { const r = await API.get(`/api/users/${u.id}/activity`); setActivity(r.data.activity || []); }
    catch (e: any) { showToast('error', e.response?.data?.message || 'Could not load activity'); setActivity([]); }
  };
  const ACTION_LABEL: Record<string, string> = {
    LOGIN: 'Signed in', CREATE: 'Created', UPDATE: 'Updated', DELETE: 'Deleted', ROLE_CHANGE: 'Role changed',
    ACTIVATE: 'Activated', DEACTIVATE: 'Deactivated', PASSWORD_CHANGE: 'Changed own password', PASSWORD_RESET: 'Password reset by admin',
  };
  // Server-side per-field messages for the create/edit modal (express-validator's errors array).
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  // Generated temporary passwords are shown in clear so the admin can copy them.
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const applyServerErrors = (error: any, fallback: string) => {
    const list: Array<{ path?: string; param?: string; msg: string }> = error?.response?.data?.errors || [];
    const map: Record<string, string> = {};
    for (const e of list) { const k = e.path || e.param; if (k && !map[k]) map[k] = e.msg; }
    setFieldErrors(map);
    const msg = error?.response?.data?.message || fallback;
    showToast('error', Object.keys(map).length ? `${msg}: ${Object.values(map).join(' ')}` : msg);
  };
  const copyText = async (text: string) => {
    try { await navigator.clipboard.writeText(text); showToast('success', 'Copied to the clipboard'); }
    catch { showToast('error', 'Could not copy; select the text and copy it by hand'); }
  };

  const isAdmin = currentUser?.role === 'ADMIN';

  useEffect(() => {
    fetchUsers();
  }, []);

  const [loadError, setLoadError] = useState('');
  // Find and sort: all users are loaded, so this is client-side.
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('');
  type SortKey = 'name' | 'role' | 'organization' | 'lastLogin' | 'createdAt';
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'lastLogin' || k === 'createdAt' ? 'desc' : 'asc'); }
  };
  const visibleUsers = users
    .filter(u => {
      const q = search.trim().toLowerCase();
      if (q && !`${u.firstName} ${u.lastName} ${u.email} ${u.organization || ''}`.toLowerCase().includes(q)) return false;
      if (roleFilter && u.role !== roleFilter) return false;
      if (statusFilter === 'active' && !u.isActive) return false;
      if (statusFilter === 'inactive' && u.isActive) return false;
      return true;
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const val = (u: User) => sortKey === 'name' ? `${u.firstName} ${u.lastName}`.toLowerCase()
        : sortKey === 'role' ? u.role
        : sortKey === 'organization' ? (u.organization || '').toLowerCase()
        : sortKey === 'lastLogin' ? (u.lastLogin || '')
        : u.createdAt;
      const x = val(a), y = val(b);
      return x < y ? -dir : x > y ? dir : 0;
    });
  const SortHeader: React.FC<{ k: SortKey; children: React.ReactNode }> = ({ k, children }) => (
    <th scope="col" aria-sort={sortKey === k ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
      <button type="button" onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-800 dark:hover:text-gray-200">
        {children}
        <span aria-hidden="true" className={sortKey === k ? 'text-gray-700 dark:text-gray-200' : 'text-gray-300 dark:text-gray-600'}>{sortKey === k && sortDir === 'desc' ? '↓' : '↑'}</span>
      </button>
    </th>
  );
  const fetchUsers = async () => {
    setLoading(true);
    setLoadError('');
    try {
      setUsers(await fetchAllUsers());
    } catch (error: any) {
      setLoadError(error?.response?.data?.message || error?.message || 'Could not load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    const problems = passwordProblems(formData.password);
    if (problems.length) {
      setFieldErrors({ password: `Password needs: ${problems.map(r => r.label.toLowerCase()).join(', ')}.` });
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      await API.post('/api/users', {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        organization: formData.organization || undefined,
        countries: formData.countries
      });
      await fetchUsers();
      setShowCreateModal(false);
      resetForm();
      showToast('success', `User created: ${formData.firstName} ${formData.lastName}`);
    } catch (error: any) {
      applyServerErrors(error, 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingUser) return;
    if (formData.role !== editingUser.role) {
      const ok = await confirm({
        title: `Change ${editingUser.firstName}'s role to ${formData.role.charAt(0) + formData.role.slice(1).toLowerCase()}?`,
        message: `${editingUser.role.charAt(0) + editingUser.role.slice(1).toLowerCase()} → ${formData.role.charAt(0) + formData.role.slice(1).toLowerCase()}. ${ROLE_DESCRIPTIONS[formData.role]} The change applies the next time they sign in.`,
        confirmLabel: 'Change role',
        cancelLabel: 'Keep as is',
        variant: formData.role === 'ADMIN' ? 'danger' : 'default',
      });
      if (!ok) return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      await API.put(`/api/users/${editingUser.id}`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        organization: formData.organization || undefined,
        countries: formData.countries
      });
      await fetchUsers();
      setEditingUser(null);
      resetForm();
      showToast('success', 'User updated');
    } catch (error: any) {
      applyServerErrors(error, 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const openResetPasswordModal = (user: User) => {
    setResetPasswordUser(user);
    setResetPasswordValue('');
    setResetPasswordConfirm('');
    setResetPasswordError('');
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPasswordUser) return;
    setResetPasswordError('');

    const problems = passwordProblems(resetPasswordValue);
    if (problems.length) {
      setResetPasswordError(`Password needs: ${problems.map(r => r.label.toLowerCase()).join(', ')}.`);
      return;
    }
    if (resetPasswordValue !== resetPasswordConfirm) {
      setResetPasswordError('Passwords do not match');
      return;
    }

    try {
      setResetPasswordLoading(true);
      await API.put(`/api/users/${resetPasswordUser.id}/password`, {
        newPassword: resetPasswordValue,
      });
      setResetPasswordUser(null);
      setResetPasswordValue('');
      setResetPasswordConfirm('');
      showToast('success', 'Password reset successfully');
    } catch (error: any) {
      setResetPasswordError(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    const action = user.isActive ? 'deactivate' : 'activate';
    const confirmed = await confirm({
      title: `${user.isActive ? 'Deactivate' : 'Activate'} User`,
      message: `Are you sure you want to ${action} ${user.firstName} ${user.lastName}?`,
      confirmLabel: user.isActive ? 'Deactivate' : 'Activate',
      variant: user.isActive ? 'danger' : 'default'
    });
    if (!confirmed) return;

    try {
      await API.put(`/api/users/${user.id}`, { isActive: !user.isActive });
      await fetchUsers();
      showToast('success', `User ${action}d successfully`);
    } catch (error: any) {
      showToast('error', error.response?.data?.message || `Failed to ${action} user`);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    const confirmed = await confirm({
      title: 'Delete User',
      message: `Delete ${userName} permanently? This is only possible for an account with no cases and no activity; otherwise deactivate it and its history stays intact.`,
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await API.delete(`/api/users/${userId}`);
      await fetchUsers();
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      showToast('error', error.response?.data?.message || 'Failed to delete user');
    }
  };

  const resetForm = () => {
    setFieldErrors({});
    setShowPassword(false);
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: 'DOCUMENTER',
      password: '',
      organization: '',
      countries: []
    });
    setCountryInput('');
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (user: User) => {
    setFormData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      password: '',
      organization: user.organization || '',
      countries: user.countries || []
    });
    setCountryInput('');
    setEditingUser(user);
  };

  // Colour rule: badges are neutral; colour is kept for meaning (red = danger). A role is a fact,
  // not a warning.
  const getRoleBadgeColor = (_role: string) => 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';

  if (!isAdmin) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Access Denied</h2>
        <p className="text-gray-600 dark:text-gray-400">You need admin privileges to access user management.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">User Management</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage system users and their roles
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add User
        </button>
      </div>

      {/* Find: search, role and status; counts reflect the filter */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="text"
          inputMode="search"
          autoComplete="off"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email or organisation"
          aria-label="Search users"
          className="w-full sm:w-72 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm text-sm py-1.5 px-3 focus:border-emerald-500 focus:ring-emerald-500"
        />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} aria-label="Filter by role" className="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm text-sm py-1.5 px-3 focus:border-emerald-500 focus:ring-emerald-500">
          <option value="">All roles</option>
          <option value="ADMIN">Admin</option>
          <option value="APPROVER">Approver</option>
          <option value="RESEARCHER">Researcher</option>
          <option value="DOCUMENTER">Documenter</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as '' | 'active' | 'inactive')} aria-label="Filter by status" className="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm text-sm py-1.5 px-3 focus:border-emerald-500 focus:ring-emerald-500">
          <option value="">Active and inactive</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <span className="text-sm text-gray-500 dark:text-gray-400">{visibleUsers.length === users.length ? `${users.length} users` : `${visibleUsers.length} of ${users.length} users`}</span>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <SortHeader k="name">User</SortHeader>
              <SortHeader k="role">Role</SortHeader>
              <SortHeader k="organization">Organisation</SortHeader>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                MFA
              </th>
              <SortHeader k="lastLogin">Last login</SortHeader>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center">
                  <div className="inline-flex items-center" role="status">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600 mr-2" aria-hidden="true"></div>
                    Loading users...
                  </div>
                </td>
              </tr>
            ) : loadError ? (
              <tr>
                <td colSpan={7} className="px-6 py-6 text-center">
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">Couldn't load users</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{loadError}</p>
                  <button type="button" onClick={fetchUsers} className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-300 underline hover:no-underline">Try again</button>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  No users yet. Use "Add User" to create the first account.
                </td>
              </tr>
            ) : visibleUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  No users match. <button type="button" onClick={() => { setSearch(''); setRoleFilter(''); setStatusFilter(''); }} className="underline hover:no-underline">Clear the filters</button>
                </td>
              </tr>
            ) : (
              visibleUsers.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="mr-3">
                        <UserAvatar user={user} size="sm" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {user.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 max-w-[14rem]">
                    {user.organization || <span className="text-gray-400 dark:text-gray-500">—</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.isActive
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        : 'bg-gray-50 text-gray-500 border border-dashed border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600'
                    }`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {user.isActive && user.mustChangePassword && (
                      <span className="ml-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="On a temporary password; first-login setup not completed">Setup pending</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.mfaEnabled
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        : 'bg-gray-50 text-gray-500 border border-dashed border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600'
                    }`}>
                      {user.mfaEnabled ? 'Enabled' : 'Not set up'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.lastLogin
                      ? <span title={formatIsoDate(user.lastLogin) || user.lastLogin}>{formatIsoDate(user.lastLogin)}</span>
                      : 'Never'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openActivity(user)}
                        className="rounded p-1 text-gray-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-gray-100 dark:hover:bg-gray-700"
                        title="Activity"
                        aria-label={`Activity of ${user.firstName} ${user.lastName}`}
                      >
                        <ClockIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => openEditModal(user)}
                        className="rounded p-1 text-gray-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-gray-100 dark:hover:bg-gray-700"
                        title="Edit user"
                        aria-label={`Edit ${user.firstName} ${user.lastName}`}
                      >
                        <PencilIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => openResetPasswordModal(user)}
                        className="rounded p-1 text-gray-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-gray-100 dark:hover:bg-gray-700"
                        title="Reset password"
                        aria-label={`Reset password for ${user.firstName} ${user.lastName}`}
                      >
                        <KeyIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      {user.id !== currentUser?.id && (
                        <button
                          onClick={() => handleToggleActive(user)}
                          className="rounded p-1 text-gray-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-gray-100 dark:hover:bg-gray-700"
                          title={user.isActive ? 'Deactivate user' : 'Activate user'}
                          aria-label={`${user.isActive ? 'Deactivate' : 'Activate'} ${user.firstName} ${user.lastName}`}
                        >
                          {user.isActive ? (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                          ) : (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </button>
                      )}
                      {user.id !== currentUser?.id && (
                        <button
                          onClick={() => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`)}
                          className="rounded p-1 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-950/30"
                          title="Delete user"
                          aria-label={`Delete ${user.firstName} ${user.lastName}`}
                        >
                          <TrashIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit User Modal */}
      {(showCreateModal || editingUser) && (
        <Modal title={editingUser ? 'Edit User' : 'Create New User'} onClose={() => { setShowCreateModal(false); setEditingUser(null); resetForm(); }}>
            
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
              {!editingUser && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
                  </div>
                  <div className="mb-4">
                    <label htmlFor="new-user-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Temporary password</label>
                    <div className="mt-1 flex gap-2">
                      <input
                        id="new-user-password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        autoComplete="new-password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 font-mono"
                      />
                      <button type="button" onClick={() => { setFormData(f => ({ ...f, password: generateTemporaryPassword() })); setShowPassword(true); }} className="shrink-0 px-2.5 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Generate</button>
                      <button type="button" disabled={!formData.password} onClick={() => copyText(formData.password)} className="shrink-0 px-2.5 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">Copy</button>
                    </div>
                    <PasswordChecklist password={formData.password} />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Pass it on securely and ask the user to change it at first sign-in.</p>
                    {fieldErrors.password && <p className="mt-1 text-xs text-red-600">{fieldErrors.password}</p>}
                  </div>
                </>
              )}
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">First Name</label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                />
              {fieldErrors.firstName && <p className="mt-1 text-xs text-red-600">{fieldErrors.firstName}</p>}
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last Name</label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                />
              {fieldErrors.lastName && <p className="mt-1 text-xs text-red-600">{fieldErrors.lastName}</p>}
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Organization</label>
                <input
                  type="text"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  placeholder="Organization name (optional)"
                />
              {fieldErrors.organization && <p className="mt-1 text-xs text-red-600">{fieldErrors.organization}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                {editingUser && editingUser.id === currentUser?.id && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 mb-1">This is your own account. Your role and status can only be changed by another admin.</p>
                )}
                <select
                  value={formData.role}
                  disabled={!!editingUser && editingUser.id === currentUser?.id}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                >
                  <option value="DOCUMENTER">Documenter</option>
                  <option value="RESEARCHER">Researcher</option>
                  <option value="APPROVER">Approver</option>
                  <option value="ADMIN">Admin</option>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{ROLE_DESCRIPTIONS[formData.role]}</p>
              </div>

              {(formData.role === 'RESEARCHER' || formData.role === 'DOCUMENTER') ? (
              <div className="mb-4">
                <label htmlFor="user-countries" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Country scope</label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-1">
                  {formData.role === 'RESEARCHER'
                    ? 'A Researcher sees cases from these countries only. Leave empty to give access to every country.'
                    : 'A Documenter sees only the cases they submitted themselves; a country scope narrows that further. Usually left empty.'}
                </p>
                <div className="relative">
                  <input
                    id="user-countries"
                    type="text"
                    role="combobox"
                    aria-expanded={!!countryInput.trim()}
                    aria-controls="user-countries-list"
                    aria-autocomplete="list"
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                    placeholder="Type to find a country, then pick it from the list"
                    value={countryInput}
                    onChange={(e) => setCountryInput(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter picks the first match; free text is never added, so a typo can't
                      // scope someone to a country that doesn't exist.
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const first = countryOptions.find(c => c.toLowerCase().includes(countryInput.trim().toLowerCase()) && !formData.countries.includes(c));
                        if (first) setFormData(prev => ({ ...prev, countries: [...prev.countries, first] }));
                        setCountryInput('');
                      }
                      if (e.key === 'Escape') setCountryInput('');
                    }}
                  />
                  {countryInput.trim() && (
                    <ul id="user-countries-list" role="listbox" className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {countryOptions
                        .filter(c => c.toLowerCase().includes(countryInput.toLowerCase()) && !formData.countries.includes(c))
                        .slice(0, 10)
                        .map(c => (
                          <li key={c} role="option" aria-selected={false}>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, countries: [...prev.countries, c] }));
                                setCountryInput('');
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              {c}
                            </button>
                          </li>
                        ))}
                      {countryOptions.filter(c => c.toLowerCase().includes(countryInput.toLowerCase()) && !formData.countries.includes(c)).length === 0 && (
                        <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No matching countries</li>
                      )}
                    </ul>
                  )}
                </div>
                {formData.countries.length > 0 ? (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.countries.map(c => (
                      <span key={c} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                        {c}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, countries: prev.countries.filter(x => x !== c) }))}
                          className="ml-1 text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
                          aria-label={`Remove ${c}`}
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">No scope: every country.</p>
                )}
              </div>
              ) : (
                <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">{formData.role === 'ADMIN' ? 'Admins' : 'Approvers'} see every case; no country scope applies.</p>
              )}

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingUser(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (editingUser ? 'Saving…' : 'Creating…') : (editingUser ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* Activity Modal */}
      {activityUser && (
        <Modal title={`Activity · ${activityUser.firstName} ${activityUser.lastName}`} description="What this account did, and what was done to it. Newest first, last 100." onClose={() => setActivityUser(null)} size="lg">
          {activity === null ? (
            <p className="text-sm text-gray-500 dark:text-gray-400" role="status">Loading…</p>
          ) : activity.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No activity recorded yet. Sign-ins and account changes are recorded from now on.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[60vh] overflow-y-auto -mx-1 px-1">
              {activity.map((a: any) => {
                const onSelf = a.entityType === 'User' && a.entityId === activityUser.id;
                const actor = a.user ? `${a.user.firstName} ${a.user.lastName}` : 'Unknown';
                const what = a.entityType === 'User'
                  ? (ACTION_LABEL[a.action] || a.action)
                  : `${a.action.charAt(0) + a.action.slice(1).toLowerCase()} ${a.entityType.toLowerCase()}${a.case?.caseNumber ? ` ${a.case.caseNumber}` : ''}`;
                const detail = a.entityType === 'User' && a.newValues && typeof a.newValues === 'object'
                  ? Object.entries(a.newValues).filter(([k]) => k !== 'mfa').map(([k, v]) => `${k}: ${a.oldValues && a.oldValues[k] !== undefined ? `${JSON.stringify(a.oldValues[k])} → ` : ''}${JSON.stringify(v)}`).join('; ')
                  : '';
                return (
                  <li key={a.id} className="py-2 text-sm">
                    <div className="flex flex-wrap justify-between gap-x-3">
                      <span className="text-gray-900 dark:text-gray-100">
                        {what}{onSelf && a.user?.id !== activityUser.id ? <span className="text-gray-500 dark:text-gray-400"> by {actor}</span> : null}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400" title={formatIsoDate(a.createdAt) || a.createdAt}>{formatIsoDate(a.createdAt)}</span>
                    </div>
                    {detail && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-all">{detail}</div>}
                  </li>
                );
              })}
            </ul>
          )}
        </Modal>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <Modal
          title="Reset Password"
          description={`Set a temporary password for ${resetPasswordUser.firstName} ${resetPasswordUser.lastName}. Pass it on securely and ask them to change it at first sign-in.`}
          onClose={() => setResetPasswordUser(null)}
        >

            {resetPasswordError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 dark:bg-red-950/40 dark:border-red-800 dark:text-red-300 rounded text-sm" role="alert">
                {resetPasswordError}
              </div>
            )}

            <form onSubmit={handleResetPassword}>
              <div className="mb-4">
                <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">New password</label>
                <div className="mt-1 flex gap-2">
                  <input
                    id="reset-password"
                    type={showResetPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    value={resetPasswordValue}
                    onChange={(e) => setResetPasswordValue(e.target.value)}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 font-mono"
                  />
                  <button type="button" onClick={() => { const pw = generateTemporaryPassword(); setResetPasswordValue(pw); setResetPasswordConfirm(pw); setShowResetPassword(true); }} className="shrink-0 px-2.5 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Generate</button>
                  <button type="button" disabled={!resetPasswordValue} onClick={() => copyText(resetPasswordValue)} className="shrink-0 px-2.5 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">Copy</button>
                </div>
                <PasswordChecklist password={resetPasswordValue} />
              </div>
              <div className="mb-4">
                <label htmlFor="reset-password-confirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm new password</label>
                <input
                  id="reset-password-confirm"
                  type={showResetPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  value={resetPasswordConfirm}
                  onChange={(e) => setResetPasswordConfirm(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setResetPasswordUser(null)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetPasswordLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                >
                  {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
        </Modal>
      )}
    </div>
  );
};

export default Users;